import { useState, useRef, useEffect, useLayoutEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { parseLocalDate } from '../../lib/dateUtils'
import { normalizeLooseDate } from '../../lib/normalizeBookings'

type Props = {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
}

/** Approximate max height of the calendar panel for flip / clamp math. */
const CALENDAR_EST_HEIGHT = 400
const CALENDAR_MIN_WIDTH = 320

/** Local calendar day for display/selection — avoids Safari/UTC pitfalls from `new Date('YYYY-MM-DD')`. */
function dateFromValue(raw: string): Date | null {
  const t = raw.trim()
  if (!t) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return parseLocalDate(t)
  const n = normalizeLooseDate(t)
  return n ? parseLocalDate(n) : null
}

export default function DatePicker({
  label,
  value,
  onChange,
  placeholder = 'yyyy-mm-dd',
  required,
  disabled,
  className,
}: Props) {
  const errorId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [currentMonth, setCurrentMonth] = useState<Date>(() => dateFromValue(value) ?? new Date())
  const containerRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null)

  const updatePopoverPosition = () => {
    // Only require anchor — `isOpen` can be stale inside rAF; caller only invokes when open.
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - 8
    const spaceAbove = rect.top - 8
    let top = rect.bottom + 8
    if (spaceBelow < CALENDAR_EST_HEIGHT && spaceAbove > spaceBelow) {
      top = Math.max(8, rect.top - CALENDAR_EST_HEIGHT - 8)
    }
    let left = rect.left
    const maxLeft = window.innerWidth - CALENDAR_MIN_WIDTH - 8
    if (left > maxLeft) left = Math.max(8, maxLeft)
    if (left < 8) left = 8
    const maxTop = window.innerHeight - CALENDAR_EST_HEIGHT - 8
    if (top > maxTop) top = Math.max(8, maxTop)
    setPopoverPos({ top, left })
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverPos(null)
      return
    }
    let rafId: number | null = null
    const measure = () => {
      if (!anchorRef.current) {
        rafId = requestAnimationFrame(measure)
        return
      }
      rafId = null
      updatePopoverPosition()
    }
    measure()
    const onResizeOrScroll = () => measure()
    window.addEventListener('resize', onResizeOrScroll)
    window.addEventListener('scroll', onResizeOrScroll, true)
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onResizeOrScroll)
      window.removeEventListener('scroll', onResizeOrScroll, true)
    }
  }, [isOpen, currentMonth])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node
      if (containerRef.current?.contains(t)) return
      if (popoverRef.current?.contains(t)) return
      setIsOpen(false)
    }

    if (!isOpen) return

    // Defer so the same pointer sequence that opened the popover doesn’t count as “outside”.
    const attachLater = window.setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(attachLater)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Keep the calendar month in sync when the parent value becomes a valid ISO date (e.g. after blur normalize).
  useEffect(() => {
    const d = dateFromValue(value)
    if (d) setCurrentMonth(d)
  }, [value])

  const commitTypedDate = () => {
    const raw = (value ?? '').trim()
    if (!raw) {
      setParseError(null)
      return
    }
    const normalized = normalizeLooseDate(raw)
    if (normalized) {
      setParseError(null)
      if (normalized !== value) onChange(normalized)
      const d = parseLocalDate(normalized)
      if (d) setCurrentMonth(d)
      return
    }
    setParseError('Enter a valid date (e.g. 2026-04-12 or 4/12/2026)')
  }

  const handleDateSelect = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    onChange(`${year}-${month}-${day}`)
    setParseError(null)
    setIsOpen(false)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev)
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1)
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1)
      }
      return newMonth
    })
  }

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()
    const totalDays = lastDay.getDate()

    const days: (Date | null)[] = []

    for (let i = 0; i < startPadding; i++) {
      days.push(null)
    }

    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i))
    }

    return days
  }

  const calendarDays = generateCalendarDays()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const selectedDate: Date | null = value ? dateFromValue(value) : null

  const displayValue = value || ''

  const calendarPanel = isOpen && !disabled && popoverPos && (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: popoverPos.top,
        left: popoverPos.left,
      }}
      className="z-[130] bg-white dark:bg-[rgb(var(--surface-rgb))] rounded-lg border border-gray-200 dark:border-[rgb(var(--border-rgb))] shadow-lg p-4 min-w-[320px]"
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigateMonth('prev')}
          className="p-2 hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentMonth(new Date())}
            className="text-sm font-medium text-gray-900 dark:text-[rgb(var(--text-rgb))] hover:text-gray-600 dark:hover:text-[rgb(var(--muted-rgb))] transition-colors"
          >
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigateMonth('next')}
          className="p-2 hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
          <div key={index} className="text-center text-xs font-medium text-gray-400 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square" />
          }

          const isToday = date.toDateString() === today.toDateString()
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
          const isPast = date < today

          return (
            <button
              key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
              type="button"
              onClick={() => handleDateSelect(date)}
              className={`
                      aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-150
                      ${
                        isSelected
                          ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                          : isToday
                            ? 'bg-gray-100 dark:bg-[rgba(255,255,255,0.10)] text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-[rgba(255,255,255,0.15)]'
                            : isPast
                              ? 'text-gray-400 dark:text-[rgb(var(--muted-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.04)]'
                              : 'text-gray-900 dark:text-[rgb(var(--text-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)]'
                      }
                    `}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-[rgb(var(--border-rgb))]">
        <button
          type="button"
          onClick={() => {
            const t = new Date()
            handleDateSelect(t)
          }}
          className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 dark:text-[rgb(var(--muted-rgb))] hover:text-gray-900 dark:hover:text-[rgb(var(--text-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-all"
        >
          Today
        </button>
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('')
              setParseError(null)
              setIsOpen(false)
            }}
            className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 dark:text-[rgb(var(--muted-rgb))] hover:text-gray-900 dark:hover:text-[rgb(var(--text-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-all"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div className={className} ref={containerRef}>
      {label ? (
        <label className="block text-sm font-medium mb-2">
          {label}
          {required ? <span className="text-red-500 ml-1">*</span> : null}
        </label>
      ) : null}

      <div className="relative" ref={anchorRef}>
        <input
          type="text"
          value={displayValue}
          onClick={() => !disabled && setIsOpen(true)}
          onChange={(e) => {
            setParseError(null)
            onChange(e.target.value)
          }}
          onBlur={commitTypedDate}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur()
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={false}
          aria-invalid={parseError ? true : undefined}
          aria-describedby={parseError ? errorId : undefined}
          className={`ds-input pr-10 cursor-pointer ${parseError ? 'border-red-500 ring-1 ring-red-500/30 dark:border-red-500' : ''}`}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
      {parseError ? (
        <p id={errorId} className="mt-1.5 text-xs text-red-600 dark:text-red-400" role="alert">
          {parseError}
        </p>
      ) : null}

      {typeof document !== 'undefined' && calendarPanel ? createPortal(calendarPanel, document.body) : null}
    </div>
  )
}
